// 1.4.0: 流式观测指标与统计工具（纯函数 + 内存计数）

export interface StreamMetricsState {
  requestId: string;
  startTime: number;
  firstChunkAt?: number;
  chunkCount: number;
  lastSequence?: number;
  missingSequenceCount: number;
  doneReceived: boolean;
  missingDoneRecorded?: boolean;
}

export interface StreamMetricsSummary {
  requestId: string;
  startTime: number;
  endTime: number;
  totalDurationMs: number;
  firstTokenDelayMs: number | null;
  chunkCount: number;
  doneReceived: boolean;
  missingSequenceCount: number;
  lastSequence?: number;
}

export interface StreamCounters {
  total: number;
  done: number;
  missingDone: number;
  incompleteChunks: number;
  chunkRollbacks: number;
  renderErrors: number;
}

const streamCounters: StreamCounters = {
  total: 0,
  done: 0,
  missingDone: 0,
  incompleteChunks: 0,
  chunkRollbacks: 0,
  renderErrors: 0,
};

export const createRequestId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `req-${crypto.randomUUID()}`;
  }

  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const resetStreamCounters = (): void => {
  streamCounters.total = 0;
  streamCounters.done = 0;
  streamCounters.missingDone = 0;
  streamCounters.incompleteChunks = 0;
  streamCounters.chunkRollbacks = 0;
  streamCounters.renderErrors = 0;
};

export const getStreamCounters = (): StreamCounters => ({
  ...streamCounters,
});

export const createStreamMetrics = (
  requestId: string,
  startTime: number = Date.now()
): StreamMetricsState => {
  streamCounters.total += 1;

  return {
    requestId,
    startTime,
    chunkCount: 0,
    missingSequenceCount: 0,
    doneReceived: false,
  };
};

export const recordChunk = (
  metrics: StreamMetricsState,
  tag?: { sequence?: number },
  now: number = Date.now()
): void => {
  metrics.chunkCount += 1;

  if (metrics.firstChunkAt === undefined) {
    metrics.firstChunkAt = now;
  }

  if (tag?.sequence !== undefined) {
    if (metrics.lastSequence !== undefined && tag.sequence > metrics.lastSequence + 1) {
      metrics.missingSequenceCount += tag.sequence - metrics.lastSequence - 1;
    }
    metrics.lastSequence = tag.sequence;
  }
};

export const recordDone = (metrics: StreamMetricsState): void => {
  if (!metrics.doneReceived) {
    metrics.doneReceived = true;
    streamCounters.done += 1;
  }
};

export const recordMissingDone = (metrics: StreamMetricsState): void => {
  if (metrics.doneReceived || metrics.missingDoneRecorded) {
    return;
  }

  metrics.missingDoneRecorded = true;
  streamCounters.missingDone += 1;
};

export const recordIncompleteChunk = (): void => {
  streamCounters.incompleteChunks += 1;
};

export const recordChunkRollback = (): void => {
  streamCounters.chunkRollbacks += 1;
};

export const recordRenderError = (): void => {
  streamCounters.renderErrors += 1;
};

export const buildStreamSummary = (
  metrics: StreamMetricsState,
  endTime: number = Date.now()
): StreamMetricsSummary => ({
  requestId: metrics.requestId,
  startTime: metrics.startTime,
  endTime,
  totalDurationMs: endTime - metrics.startTime,
  firstTokenDelayMs:
    metrics.firstChunkAt !== undefined ? metrics.firstChunkAt - metrics.startTime : null,
  chunkCount: metrics.chunkCount,
  doneReceived: metrics.doneReceived,
  missingSequenceCount: metrics.missingSequenceCount,
  lastSequence: metrics.lastSequence,
});
